import React, { useCallback, useMemo } from 'react'

import { Trash } from 'lucide-react'
import type { FC } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'

import type {
	SelectFilterDefinition,
	TextFilterDefinition,
} from '../TableFilters/filter-shapes'
import type { CustomFilterShape } from './useFilters'
import type { OperatorDefinition } from './util'
import { getFlatFilter } from './util'

interface FilterRowProps {
	filter: CustomFilterShape
	options: Array<
		(SelectFilterDefinition | TextFilterDefinition) & {
			operators: Array<OperatorDefinition>
		}
	>
	index: number
	onChange: (newValue: CustomFilterShape, index: number) => void
}

export const FilterRow: FC<FilterRowProps> = ({
	filter,
	options,
	index,
	onChange,
}) => {
	const [column, operator, value] = getFlatFilter(filter)
	const findOperators = useCallback(
		(column: string) =>
			column && options.find(option => option.column.value === column),
		[options]
	)

	const filterConfig = useMemo(
		() => findOperators(column),
		[findOperators, column]
	)

	const columns = useMemo(
		() => options.map(option => option.column),
		[options]
	)

	const onFilterChange = useCallback(
		(
			newFilterValue: string,
			valueType: 'column' | 'operator' | 'value'
		) => {
			let newFilter: CustomFilterShape = {}
			switch (valueType) {
				case 'column':
					const operators = findOperators(newFilterValue)?.operators
					if (!operators || operators.length === 0) return

					newFilter = {
						[newFilterValue]: {
							[operators[0].value]: null,
						},
					} satisfies CustomFilterShape
					break
				case 'operator':
					if (newFilterValue === operator) return

					newFilter = {
						[column]: {
							[newFilterValue]: value,
						},
					} satisfies CustomFilterShape
					break
				case 'value':
					if (newFilterValue === value) return

					newFilter = {
						[column]: {
							[operator]: newFilterValue,
						},
					} satisfies CustomFilterShape
					break
			}

			onChange(newFilter, index)
		},
		[index, onChange, column, value, operator, findOperators]
	)

	const setColumn = useCallback(
		(col: string) => {
			if (col === column) return
			onFilterChange(col, 'column')
		},
		[onFilterChange, column]
	)

	const setOperator = useCallback(
		(newOperator: string) => {
			if (newOperator === operator) return
			onFilterChange(newOperator, 'operator')
		},
		[onFilterChange, operator]
	)

	const setValue = useCallback(
		(newValue: string) => {
			if (newValue === value) return
			onFilterChange(newValue, 'value')
		},
		[onFilterChange, value]
	)

	const setTextValue = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value),
		[setValue]
	)

	const onFilterRemove = useCallback(() => {
		onChange(null, index)
	}, [onChange, index])

	if (!column) return null

	return (
		<div
			role="row"
			key={`${column}-${value}-${operator}-filter`}
			className="grid grid-cols-1 sm:grid-cols-[0.25fr_1fr_0.5fr_1fr_auto] items-center gap-2 w-full mb-2"
		>
			<span className="text-sm text-center w-full sm:text-left">
				{index === 0 ? 'Where' : 'And'}
			</span>
			<Select value={column} onValueChange={setColumn}>
				<SelectTrigger
					aria-label="Select column"
					id="select-column"
					className="h-8 w-full"
				>
					<SelectValue placeholder="Select column" />
				</SelectTrigger>
				<SelectContent>
					{columns.map(col => (
						<SelectItem key={col.value} value={col.value}>
							{col.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			<Select
				value={operator}
				onValueChange={setOperator}
				disabled={!filterConfig}
			>
				<SelectTrigger
					aria-label="Select operator"
					id="select-operator"
					className="h-8 w-full"
				>
					<SelectValue placeholder="Select operator" />
				</SelectTrigger>
				<SelectContent>
					{filterConfig?.operators.map(op => (
						<SelectItem key={op.value} value={op.value}>
							{op.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>

			{filterConfig?.type === 'select' && (
				<Select
					value={value as string}
					onValueChange={setValue}
					disabled={!operator || !column}
				>
					<SelectTrigger
						aria-label="Select value"
						id="select-value"
						className="h-8 w-full max-w-[200px]"
						title={value as string}
					>
						<SelectValue
							placeholder="Select value"
							className="truncate"
						/>
					</SelectTrigger>
					<SelectContent className="max-w-[300px]">
						{filterConfig.values.map(val => (
							<SelectItem
								key={val.value}
								value={val.value}
								title={val.label}
								className="truncate"
							>
								{val.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			)}

			{filterConfig?.type === 'text' && (
				<Input
					autoFocus
					aria-label="text filter value"
					value={value as string}
					placeholder="Enter a value"
					onChange={setTextValue}
					className="w-full h-8"
				/>
			)}
			<Button
				variant="outline"
				size="icon"
				role="button"
				aria-label="Remove current filter"
				aria-hidden="false"
				onClick={onFilterRemove}
				className="h-8 w-8 flex items-center justify-center"
			>
				<Trash />
			</Button>
		</div>
	)
}
